//-- Copyright (c) 2021 TMT Insights Group, LLC
import React from 'react';
import { deepmerge } from '@mui/utils';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  darkBaseTheme as defaultDarkBaseTheme,
  lightBaseTheme as defaultLightBaseTheme,
  fullWidthTheme,
  fullWidthThemeNoAnimate
} from '@/constants/baseThemes';
import { useAppSelector } from '@/redux/hooks';
import { selectUserDarkMode, selectUserFullWidthMode } from '@/redux/selectors/authSelectors';
import { selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import { UIWidth } from '@/components/_common/ToggleFullWidth';
import config from './configs';

interface ThemeProps {
  children: React.ReactNode;
}

const FocusTheme = ({ children }: ThemeProps) => {
  const tableKey = useAppSelector(selectCurrentTableKey);
  const darkMode = useAppSelector(selectUserDarkMode);
  const fullWidthMode = useAppSelector(selectUserFullWidthMode);
  const fullWidth = fullWidthMode && tableKey && fullWidthMode.includes(tableKey);
  const darkBaseTheme = config?.theme?.darkTheme || defaultDarkBaseTheme;
  const lightBaseTheme = config?.theme?.lightTheme || defaultLightBaseTheme;

  let theme = darkMode ? darkBaseTheme : lightBaseTheme;

  if (fullWidth === UIWidth.FULL) {
    theme = deepmerge(theme, fullWidthTheme);
  } else if (fullWidth === UIWidth.COMPACT_NO_ANIMATE) {
    theme = deepmerge(theme, fullWidthThemeNoAnimate);
  }

  window.theme = theme;

  return React.useMemo(
    () => (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>{children}</ThemeProvider>
      </StyledEngineProvider>
    ),
    [darkMode, fullWidth]
  );
};

export default FocusTheme;
